import { IBEError } from '@common/interfaces/be-error.interface';
import {
    IChangePassword,
    ICredentials,
    IForceChangePassword,
    IForceChangePasswordResponse,
    IForgotPassword,
    IForgotPasswordConfirm,
    ILogedInResponse,
    IRefresh,
    IRefreshResponse,
    IVerifyLogedInResponse,
    registrationApiRequest,
    TRegistrationV1AuthVerifyPostError,
    TRegistrationV1ChangePasswordForcePostError,
    TRegistrationV1ChangePasswordPostError,
    TRegistrationV1ForgotPasswordConfirmPostError,
    TRegistrationV1ForgotPasswordPostError,
    TRegistrationV1LoginPostError,
    TRegistrationV1RefreshPostError,
    TRegistrationV1SignOutPostError,
} from '@src/api/registration-generated-api';
import { useMutation, UseMutationResult } from 'react-query';

const NAME = 'auth';

const useLogin = (): UseMutationResult<ILogedInResponse, IBEError<TRegistrationV1LoginPostError>, ICredentials> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1LoginPost(props, {}).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-login`,
        },
    );
};

const useForgotPassword = (): UseMutationResult<void, IBEError<TRegistrationV1ForgotPasswordPostError>, IForgotPassword> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1ForgotPasswordPost(props).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-login`,
        },
    );
};

const useRecoveryPassword = (): UseMutationResult<
    IForceChangePasswordResponse,
    IBEError<TRegistrationV1ChangePasswordForcePostError>,
    IForceChangePassword
> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1ChangePasswordForcePost(props, {}).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-login`,
        },
    );
};

const useChangePassword = (): UseMutationResult<void, IBEError<TRegistrationV1ChangePasswordPostError>, IChangePassword> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1ChangePasswordPost(props, {}).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-login`,
        },
    );
};

const useConfirmForgotPassword = (): UseMutationResult<
    void,
    IBEError<TRegistrationV1ForgotPasswordConfirmPostError>,
    IForgotPasswordConfirm
> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1ForgotPasswordConfirmPost(props).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-login`,
        },
    );
};

const useVerify = (): UseMutationResult<IVerifyLogedInResponse, IBEError<TRegistrationV1AuthVerifyPostError>, void> => {
    return useMutation(
        () => {
            return registrationApiRequest.registrationV1AuthVerifyPost({}).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-login`,
        },
    );
};

const useLogout = (): UseMutationResult<void, IBEError<TRegistrationV1SignOutPostError>, void> => {
    return useMutation(
        () => {
            return registrationApiRequest.registrationV1SignOutPost({}).then((r) => {
                return r.data;
            });
        },
        {
            mutationKey: `${NAME}-logout`,
        },
    );
};

const useRefreshToken = (): UseMutationResult<IRefreshResponse, IBEError<TRegistrationV1RefreshPostError>, IRefresh> => {
    return useMutation(
        (props) => {
            return registrationApiRequest
                .registrationV1RefreshPost(props, { ['App-Client-Id']: undefined })
                .then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-refresh-token`,
        },
    );
};

export const auth = {
    useVerify,
    useChangePassword,
    useLogin,
    useRefreshToken,
    useLogout,
    useForgotPassword,
    useConfirmForgotPassword,
    useRecoveryPassword,
};
